'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { motion } from 'motion/react';
import { RoomAudioRenderer, RoomContext, StartAudio } from '@livekit/components-react';
import { ErrorMessage } from '@/components/embed-popup/error-message';
import { PopupView } from '@/components/embed-popup/popup-view';
import { Trigger } from '@/components/embed-popup/trigger';
import useConnectionDetails from '@/hooks/use-connection-details';
const PopupViewMotion = motion.create(PopupView);
function AgentClient({ appConfig, showTriggerButton = true }) {
    const isAnimating = useRef(false);
    const room = useMemo(() => new Room(), []);
    const [popupOpen, setPopupOpen] = useState(false);
    const [error, setError] = useState(null);
    const [selectedAgentName, setSelectedAgentName] = useState('CustomerSupportAIAgent');
    const [accessToken, setAccessToken] = useState('');
    const { connectionDetails, refreshConnectionDetails, existingOrRefreshConnectionDetails } = useConnectionDetails(appConfig, selectedAgentName, accessToken);
    const handleTogglePopup = () => {
        if (isAnimating.current) {
            // prevent re-opening before room has disconnected
            return;
        }
        setError(null);
        setPopupOpen((open) => !open);
    };
    const handlePanelAnimationStart = () => {
        isAnimating.current = true;
    };
    const handlePanelAnimationComplete = () => {
        isAnimating.current = false;
        if (!popupOpen && room.state !== 'disconnected') {
            room.disconnect();
        }
    };
    useEffect(() => {
        const onDisconnected = () => {
            setPopupOpen(false);
            refreshConnectionDetails();
        };
        const onMediaDevicesError = (error) => {
            setError({
                title: 'Encountered an error with your media devices',
                description: `${error.name}: ${error.message}`,
            });
        };
        room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);
        room.on(RoomEvent.Disconnected, onDisconnected);
        return () => {
            room.off(RoomEvent.Disconnected, onDisconnected);
            room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
        };
    }, [room, refreshConnectionDetails]);
    // Expose toggle function to window for external buttons
    useEffect(() => {
        window.livekitVoiceAgent = {
            toggle: handleTogglePopup,
            isOpen: popupOpen,
            currentAgentName: selectedAgentName,
            setAgentName: async (name) => {
                // Set the agent name first
                setSelectedAgentName(name);
                // Wait for the state to update and connection details to be ready
                await new Promise((resolve) => setTimeout(resolve, 100));
            },
            setAccessToken: (token) => {
                setAccessToken(token);
            },
        };
        return () => {
            delete window.livekitVoiceAgent;
        };
    }, [handleTogglePopup, popupOpen, selectedAgentName]);
    useEffect(() => {
        if (!popupOpen) {
            return;
        }
        if (!connectionDetails) {
            // Don't show error immediately - give time for connection details to fetch
            const timeoutId = setTimeout(() => {
                if (!connectionDetails && popupOpen) {
                    setError({
                        title: 'Error fetching connection details',
                        description: 'Please try again later',
                    });
                }
            }, 2000); // Wait 2 seconds before showing error
            return () => clearTimeout(timeoutId);
        }
        // Clear any previous errors now that we have connection details
        setError(null);
        if (room.state !== 'disconnected') {
            return;
        }
        const connect = async () => {
            Promise.all([
                room.localParticipant.setMicrophoneEnabled(true, undefined, {
                    preConnectBuffer: appConfig.isPreConnectBufferEnabled,
                }),
                existingOrRefreshConnectionDetails().then((connectionDetails) => room.connect(connectionDetails.serverUrl, connectionDetails.participantToken)),
            ]).catch((error) => {
                if (error instanceof Error) {
                    console.error('Error connecting to agent:', error);
                    setError({
                        title: 'There was an error connecting to the agent',
                        description: `${error.name}: ${error.message}`,
                    });
                }
            });
        };
        connect();
    }, [
        room,
        popupOpen,
        connectionDetails,
        existingOrRefreshConnectionDetails,
        appConfig.isPreConnectBufferEnabled,
    ]);
    return (_jsxs(RoomContext.Provider, { value: room, children: [_jsx(RoomAudioRenderer, {}), _jsx(StartAudio, { label: "Start Audio" }), (showTriggerButton || popupOpen) && (_jsx(Trigger, { error: error, popupOpen: popupOpen, onToggle: handleTogglePopup })), _jsx(motion.div, { inert: !popupOpen, initial: {
                    opacity: 0,
                    translateY: 8,
                }, animate: {
                    opacity: popupOpen ? 1 : 0,
                    translateY: popupOpen ? 0 : 8,
                }, transition: {
                    type: 'spring',
                    bounce: 0,
                    duration: popupOpen ? 1 : 0.2,
                }, onAnimationStart: handlePanelAnimationStart, onAnimationComplete: handlePanelAnimationComplete, className: "fixed right-4 bottom-20 left-4 z-50 md:left-auto", children: _jsx("div", { className: "bg-bg1 dark:bg-bg2 border-separator1 dark:border-separator2 ml-auto h-[480px] w-full rounded-[28px] border border-solid drop-shadow-md md:w-[360px]", children: _jsxs("div", { className: "relative h-full w-full", children: [_jsx(ErrorMessage, { error: error }), !error && (_jsx(PopupViewMotion, { appConfig: appConfig, initial: { opacity: 1 }, animate: { opacity: error === null ? 1 : 0 }, transition: {
                                    type: 'tween',
                                    ease: 'linear',
                                    duration: 0.2,
                                }, disabled: !popupOpen, sessionStarted: popupOpen, onEmbedError: setError, className: "absolute inset-0" }))] }) }) })] }));
}
export default AgentClient;
